import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Package, Download, RefreshCw, Mail, ExternalLink } from "lucide-react";
import {
  listPalletBalance, listPalletMovements, loadPicklists,
} from "@/lib/api";
import { useViewAsClient } from "@/lib/viewAsClient";

export default function PalletReconciliation() {
  const isSuper = useAuth().hasRole(["super_admin"]);
  const [balances,  setBalances]  = useState([]);
  const [movements, setMovements] = useState([]);
  const [brands,    setBrands]    = useState([]);   // [{value,label}] from picklist
  const [brand,     setBrand]     = useState("");
  const [loading,   setLoading]   = useState(true);
  const [drillRow,  setDrillRow]  = useState(null); // {counterparty, brand} balance row
  const [sendingDigest, setSendingDigest] = useState(false);
  const { viewAsClientId } = useViewAsClient();
  const [tick,      setTick]      = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [bal, mov, p] = await Promise.all([
        listPalletBalance({ brand: brand || null, clientId: viewAsClientId || null }),
        listPalletMovements({ brand: brand || null, clientId: viewAsClientId || null }),
        loadPicklists(),
      ]);
      if (cancelled) return;
      if (bal.error) toast.error(bal.error.message);
      setBalances(bal.data || []);
      setMovements(mov.data || []);
      setBrands(p?.pallet_type || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [brand, viewAsClientId, tick]);

  const refresh = () => setTick((k) => k + 1);

  const totals = useMemo(() => {
    const t = { in: 0, out: 0 };
    for (const b of balances) {
      t.in  += Number(b.pallets_received   || 0);
      t.out += Number(b.pallets_dispatched || 0);
    }
    return { ...t, net: t.in - t.out };
  }, [balances]);

  const variances = movements.filter((m) => m.variance_in || m.variance_out);

  const exportCSV = () => {
    const header = [
      "client_id","brand","counterparty",
      "pallets_received","pallets_dispatched","net_balance",
      "last_movement_date","movement_count",
    ];
    const esc = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [header.join(",")];
    for (const b of balances) {
      lines.push([
        esc(b.client_id), esc(b.brand), esc(b.counterparty),
        b.pallets_received || 0, b.pallets_dispatched || 0, b.net_balance || 0,
        esc(b.last_movement_date), b.movement_count || 0,
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pallet-reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${balances.length} rows.`);
  };

  return (
    <div data-testid="pallet-reconciliation-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Package className="h-6 w-6" /> Pallet reconciliation
          </h1>
          <p className="text-sm text-slate-500">
            Daily running balance per customer per brand. Net = received − dispatched.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-44">
            <Select value={brand || "_all_"} onValueChange={(v) => setBrand(v === "_all_" ? "" : v)}>
              <SelectTrigger data-testid="pallet-brand-filter"><SelectValue placeholder="All brands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All brands</SelectItem>
                {brands.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isSuper && <Button variant="outline" onClick={async () => {
              try {
                setSendingDigest(true);
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/send-pallet-digests`;
                const sess = (await import("@/lib/supabaseClient")).supabase;
                const { data: { session } } = await sess.auth.getSession();
                const r = await fetch(url, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
                  },
                });
                const j = await r.json();
                if (!r.ok) throw new Error(j?.detail || "Digest send failed");
                toast.success(`Digest sent — ${j.delivered}/${j.enqueued} delivered to ${j.tenants} tenant(s).`);
              } catch (e) { toast.error(e.message); }
              finally { setSendingDigest(false); }
            }} disabled={sendingDigest} data-testid="pallet-send-digest">
            <Mail className="h-4 w-4 mr-1.5" /> {sendingDigest ? "Sending…" : "Send digest now"}
          </Button>}
          <Button variant="outline" onClick={refresh} data-testid="pallet-refresh">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <Button onClick={exportCSV} disabled={!balances.length} data-testid="pallet-export">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Received" value={totals.in}  tone="green"  testid="stat-in" />
        <Stat label="Dispatched" value={totals.out} tone="rose" testid="stat-out" />
        <Stat label="Net balance"
              value={totals.net}
              tone={totals.net >= 0 ? "green" : "rose"}
              suffix={totals.net > 0 ? "+" : (totals.net < 0 ? "−" : "")}
              testid="stat-net" />
      </div>

      {/* Balance per (counterparty × brand) */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">
            Balance per customer × brand <span className="text-sm font-normal text-slate-500">({balances.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-400">Loading…</div>
          ) : balances.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400" data-testid="pallet-empty">
              No pallet movements recorded yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Dispatched</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead>Last movement</TableHead>
                  <TableHead className="text-right">Bookings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {balances.map((b, i) => (
                  <TableRow
                    key={`${b.counterparty}-${b.brand}-${i}`}
                    data-testid={`pallet-row-${b.brand}-${i}`}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setDrillRow(b)}
                  >
                    <TableCell className="text-sm font-medium">{b.counterparty}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px] font-mono">{b.brand}</Badge>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{b.pallets_received || 0}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{b.pallets_dispatched || 0}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums font-semibold">
                      <span className={(b.net_balance || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}>
                        {b.net_balance > 0 ? "+" : ""}{b.net_balance || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{b.last_movement_date || "—"}</TableCell>
                    <TableCell className="text-right text-xs text-slate-500">{b.movement_count || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Variances (auto-flagged) */}
      {variances.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base text-rose-700">
              Variances (FLD ≠ office) — {variances.length}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Counterparty</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead className="text-right">IN (qty)</TableHead>
                  <TableHead className="text-right">OUT (qty)</TableHead>
                  <TableHead>Flag</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {variances.map((m) => (
                  <TableRow key={m.booking_id} data-testid={`variance-${m.booking_id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{m.movement_date}</TableCell>
                    <TableCell className="font-mono text-xs">{m.reference_number}</TableCell>
                    <TableCell className="text-sm">
                      {m.direction === "inbound" ? m.sender : m.receiver}
                    </TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px] font-mono">{m.brand}</Badge></TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{m.qty_in}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{m.qty_out}</TableCell>
                    <TableCell>
                      {m.variance_in  && <Badge className="bg-rose-100 text-rose-700 text-[10px] mr-1">IN ≠</Badge>}
                      {m.variance_out && <Badge className="bg-rose-100 text-rose-700 text-[10px]">OUT ≠</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Drill-down modal: bookings making up one balance row */}
      <Dialog open={!!drillRow} onOpenChange={(o) => !o && setDrillRow(null)}>
        <DialogContent className="max-w-3xl" data-testid="pallet-drilldown">
          <DialogHeader>
            <DialogTitle>
              {drillRow?.counterparty} · {drillRow?.brand}
            </DialogTitle>
            <DialogDescription>
              All movements making up the net balance of{" "}
              <span className={`font-semibold tabular-nums ${(drillRow?.net_balance ?? 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {(drillRow?.net_balance ?? 0) > 0 ? "+" : ""}{drillRow?.net_balance ?? 0}
              </span>{" "}
              pallet(s).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto -mx-6 px-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead className="text-right">IN</TableHead>
                  <TableHead className="text-right">OUT</TableHead>
                  <TableHead className="text-center">Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements
                  .filter((m) => m.brand === drillRow?.brand
                    && (m.sender === drillRow?.counterparty || m.receiver === drillRow?.counterparty))
                  .map((m) => (
                    <TableRow key={m.booking_id} data-testid={`drill-row-${m.booking_id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{m.movement_date}</TableCell>
                      <TableCell className="font-mono text-xs">
                        <Link
                          to={`/dock/checklist/${m.booking_id}`}
                          target="_blank"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          {m.reference_number}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{m.direction}</Badge></TableCell>
                      <TableCell className="text-right tabular-nums">{m.qty_in || 0}</TableCell>
                      <TableCell className="text-right tabular-nums">{m.qty_out || 0}</TableCell>
                      <TableCell className="text-center">
                        {(m.variance_in || m.variance_out) ? (
                          <Badge className="bg-rose-100 text-rose-700 text-[10px]">variance</Badge>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                {movements.filter((m) => m.brand === drillRow?.brand
                    && (m.sender === drillRow?.counterparty || m.receiver === drillRow?.counterparty)).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-slate-400 py-6">
                      No movements found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone = "slate", suffix, testid }) {
  const toneClass =
    tone === "green" ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
    tone === "rose"  ? "text-rose-700 bg-rose-50 border-rose-200" :
                       "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <Card className={`border ${toneClass}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-wider opacity-70 font-medium">{label}</div>
        <div className="text-3xl font-semibold tabular-nums mt-1">{value}</div>
        {suffix && <div className="text-xs opacity-80 mt-0.5">{suffix}</div>}
      </CardContent>
    </Card>
  );
}
